import type { ReactNode } from "react";
import { cn } from "../../utils/cn";

export interface TabItem {
  id: string;
  label: ReactNode;
  count?: number;
  disabled?: boolean;
}

interface TabsProps {
  items: TabItem[];
  value: string;
  onChange: (id: string) => void;
  variant?: "pills" | "underline";
  className?: string;
}

export function Tabs({ items, value, onChange, variant = "pills", className }: TabsProps) {
  if (variant === "underline") {
    return (
      <div className={cn("flex gap-1 overflow-x-auto border-b border-background-200", className)} role="tablist">
        {items.map((item) => {
          const active = item.id === value;
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={active}
              disabled={item.disabled}
              onClick={() => onChange(item.id)}
              className={cn(
                "relative flex cursor-pointer items-center gap-1.5 whitespace-nowrap px-3 py-2.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400/40 disabled:cursor-not-allowed disabled:text-foreground-400",
                active ? "text-primary-700 font-semibold" : "text-foreground-600 hover:text-foreground-900"
              )}
            >
              {item.label}
              {item.count !== undefined ? (
                <span className={cn("rounded-full px-1.5 text-xs tabular-nums", active ? "bg-primary-100 text-primary-800" : "bg-secondary-100 text-secondary-700")}>
                  {item.count}
                </span>
              ) : null}
              {active ? <span className="absolute inset-x-0 bottom-0 h-0.5 bg-primary-600" /> : null}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className={cn("flex flex-wrap items-center gap-1.5 border-b border-background-200 pb-3", className)} role="tablist">
      {items.map((item) => {
        const active = item.id === value;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={active}
            disabled={item.disabled}
            onClick={() => onChange(item.id)}
            className={cn(
              "flex cursor-pointer items-center gap-2 rounded-md px-3.5 py-2 text-sm font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400/40 disabled:cursor-not-allowed disabled:text-foreground-400",
              active
                ? "bg-primary-600 text-white font-semibold shadow-sm hover:bg-primary-700"
                : "bg-background-100/70 text-foreground-600 hover:bg-background-200/70 hover:text-foreground-900"
            )}
          >
            <span>{item.label}</span>
            {item.count !== undefined ? (
              <span
                className={cn(
                  "rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums",
                  active ? "bg-white/20 text-white" : "bg-background-200 text-foreground-700"
                )}
              >
                {item.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}